import React, { useEffect, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import PublicIcon from '@mui/icons-material/Public'
import { SEVV_MODAL } from '../VirtualRoom/VirtualRoomModalStyles'
import Parse from '../../services/initializeParse'
import {
  addGuestsButtonLabel,
  guestEmailError,
  isValidEmail,
  normalizeEmail,
  MAX_EXTERNAL_GUESTS,
  type ExternalGuestData,
  type GuestEmailContext,
} from './externalGuests'

export type { ExternalGuestData }

interface ExternalGuestDialogProps {
  open: boolean
  onClose: () => void
  onAdd: (guests: ExternalGuestData[]) => void
  /** Prefilled when opened from the Invitees search's "Add as external guest" option. */
  initialEmail?: string
  /** How many more guests this booking can take. */
  remainingSlots: number
  emailContext: GuestEmailContext
}

export default function ExternalGuestDialog({
  open,
  onClose,
  onAdd,
  initialEmail = '',
  remainingSlots,
  emailContext,
}: ExternalGuestDialogProps) {
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [staged, setStaged] = useState<ExternalGuestData[]>([])
  const emailInputRef = useRef<HTMLInputElement>(null)
  // Only the most recent lookup may fill fields; the user can retype the email mid-request.
  // Also skips re-looking-up the same address on every blur, since lookups are rate-limited.
  const lookupEmailRef = useRef('')

  const clearFields = () => {
    setName('')
    setCompany('')
    setEmail('')
    setEmailError('')
    lookupEmailRef.current = ''
  }

  /**
   * Prefill name/company for a Sevv user from another company or a returning guest, never
   * overwriting what was typed. Company members are rejected locally and never looked up.
   */
  const lookupProfile = (value: string) => {
    if (!isValidEmail(value)) return
    const key = normalizeEmail(value)
    if (key === lookupEmailRef.current || emailContext.companyEmails.has(key)) return
    lookupEmailRef.current = key
    Parse.Cloud.run('lookupGuestEmail', { email: key })
      .then((profile: { displayName?: string; company?: string } | null) => {
        if (!profile || lookupEmailRef.current !== key) return
        if (profile.displayName) setName((prev) => prev || profile.displayName!)
        if (profile.company) setCompany((prev) => prev || profile.company!)
      })
      .catch((err: unknown) => console.warn('[ExternalGuestDialog] Guest profile lookup failed:', err))
  }

  useEffect(() => {
    if (!open) return
    clearFields()
    setStaged([])
    if (initialEmail) {
      setEmail(initialEmail)
      lookupProfile(initialEmail)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialEmail])

  const draftFilled = [name, company, email].some((v) => v.trim().length > 0)
  const draftComplete = [name, company, email].every((v) => v.trim().length > 0)
  const atLimit = staged.length >= remainingSlots

  /** Validate the fields as one guest, or show why they can't be added. */
  const readDraft = (): ExternalGuestData | null => {
    const trimEmail = email.trim()
    const error = guestEmailError(trimEmail, {
      companyEmails: emailContext.companyEmails,
      takenEmails: new Set([...emailContext.takenEmails, ...staged.map((g) => normalizeEmail(g.email))]),
    })
    if (error) {
      setEmailError(error)
      return null
    }
    return { name: name.trim(), company: company.trim(), email: trimEmail }
  }

  const handleStageAnother = () => {
    const guest = readDraft()
    if (!guest) return
    setStaged((prev) => [...prev, guest])
    clearFields()
    emailInputRef.current?.focus()
  }

  const handleAdd = () => {
    let guests = staged
    if (draftFilled) {
      const guest = readDraft()
      if (!guest) return
      guests = [...staged, guest]
    }
    onAdd(guests)
    onClose()
  }

  const canAdd = draftFilled ? draftComplete : staged.length > 0
  const canStageAnother = draftComplete && staged.length + 1 <= remainingSlots
  const guestCount = staged.length + (draftFilled ? 1 : 0)
  const limitMessage = `A meeting can have at most ${MAX_EXTERNAL_GUESTS} external guests`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { ...SEVV_MODAL.paper, minWidth: 360, maxWidth: 440 } }}
      slotProps={{ backdrop: { sx: SEVV_MODAL.backdrop } }}
    >
      <DialogTitle sx={SEVV_MODAL.title}>Invite External Guest</DialogTitle>
      <DialogContent sx={{ ...SEVV_MODAL.content, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {staged.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} data-testid="staged-guests">
            {staged.map((g, i) => (
              <Chip
                key={`${g.email}-${i}`}
                label={`${g.name} (${g.company})`}
                title={g.email}
                icon={<PublicIcon sx={{ fontSize: 16 }} />}
                size="small"
                onDelete={() => setStaged((prev) => prev.filter((_, idx) => idx !== i))}
                sx={{ borderColor: 'var(--sevv-primary, #f97415)', color: 'text.primary' }}
                variant="outlined"
              />
            ))}
          </Box>
        )}
        {/* Email first: the lookup on blur can then fill Name and Company before the host types them. */}
        <TextField
          label="Email"
          required
          fullWidth
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setEmailError('') }}
          onBlur={() => lookupProfile(email)}
          error={!!emailError}
          helperText={emailError}
          autoFocus
          inputRef={emailInputRef}
          disabled={atLimit}
          sx={SEVV_MODAL.input}
        />
        <TextField
          label="Name"
          required
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={atLimit}
          sx={SEVV_MODAL.input}
        />
        <TextField
          label="Company"
          required
          fullWidth
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          disabled={atLimit}
          sx={SEVV_MODAL.input}
        />
        {atLimit && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {limitMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={SEVV_MODAL.actions}>
        <Button onClick={onClose} sx={SEVV_MODAL.btnSecondary} size="small">Cancel</Button>
        <Tooltip title={canStageAnother || !draftComplete ? 'Add another guest' : limitMessage} arrow>
          <span>
            <IconButton
              aria-label="Add another guest"
              onClick={handleStageAnother}
              disabled={!canStageAnother}
              size="small"
              sx={{
                color: 'var(--sevv-primary, #f97415)',
                border: '1px solid var(--sevv-primary, #f97415)',
                '&.Mui-disabled': { borderColor: 'var(--sevv-divider)', color: 'text.disabled' },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          onClick={handleAdd}
          disabled={!canAdd}
          sx={SEVV_MODAL.btnPrimary}
          size="small"
        >
          {addGuestsButtonLabel(guestCount)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
